import React, { useState, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Box,
    Typography,
    Chip,
    Stack,
    Button,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    TableSortLabel,
    InputAdornment,
    Alert,
    Pagination,
    FormControlLabel,
    Checkbox,
    useTheme,
    Autocomplete,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Grid,
} from '@mui/material';
import {
    Search,
    FilterList,
    Download,
    Clear,
    Visibility,
    VisibilityOff,
    DeleteTwoTone,
    BadgeTwoTone,
    Email,
    Print,
    Edit,
    Save,
    Cancel,
    AccessTime,
    EventAvailable,
    EventBusy,
    Schedule,
    Payment,
    Error,
    Warning,
    CheckCircle,
    MarkEmailRead,
    MarkEmailUnread,
    EmailOutlined,
    ReportProblem,
    Block,
} from '@mui/icons-material';
import ExportUtils from '../../utils/exportUtils';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import SendEmail from '../../Components/SendEmail';
import { format } from 'date-fns';

// Pure function to check if badge icon should be visible
const isBadgeEligible = (row, eventId, role, features = [], permissions = []) => {
    // Performance: Removed console.log for better performance

    // For themeRegistration feature, check isConfirmed directly without permission check
    if (features.includes('themeRegistration')) {
        return row.isConfirmed === true || row.isConfirmed === 1;
    }

    // For other features, check permission first
    if (!permissions.includes('badge-' + role)) {
        return false;
    }
    if (
        permissions.includes('badge-' + role) &&
        !features.includes('PaymentEnable') &&
        !features.includes('emailVerification')
    ) {
        return true;
    }

    // Check PaymentEnable feature
    if (features.includes('PaymentEnable')) {
        // console.log('features.includes(PaymentEnable)', features.includes('PaymentEnable'));
        // Handle different data structures for payment_status
        let paymentStatus = '';
        if (row.data && typeof row.data === 'string') {
            try {
                const parsedData = JSON.parse(row.data);
                paymentStatus = parsedData.payment_status || '';
            } catch (error) {
                console.warn('Failed to parse row.data as JSON:', error);
                paymentStatus = '';
            }
        } else if (row.data && typeof row.data === 'object') {
            paymentStatus = row.data.payment_status || '';
        } else {
            paymentStatus = row.payment_status || '';
        }

        if (paymentStatus && paymentStatus.toLowerCase() === 'success') {
            return true;
        }
    }

    // Check emailVerification feature
    if (features.includes('emailVerification')) {
        // console.log('row.status', row.status);
        // Handle different data structures for status
        let status = '';
        if (row.data && typeof row.data === 'string') {
            try {
                const parsedData = JSON.parse(row.data);
                status = parsedData.status || '';
            } catch (error) {
                console.warn('Failed to parse row.data as JSON:', error);
                status = '';
            }
        } else if (row.data && typeof row.data === 'object') {
            status = row.data.status || '';
        } else {
            status = row.status || '';
        }

        if (status && status.toLowerCase() === 'accept') {
            return true;
        }
    }

    // Check themeRegistration feature
    if (features.includes('themeRegistration')) {
        console.log('row.isConfirmed', row.isConfirmed);
        return row.isConfirmed === true;
    }

    // For normal scenarios (no paymentEnable, no emailVerification)
    // Check if $role_badge field exists and has a value
    if (!features.includes('PaymentEnable') && !features.includes('emailVerification')) {
        const badgeFieldName = `${role}_badge`;

        // Check in row data directly
        if (row[badgeFieldName] !== undefined && row[badgeFieldName] !== null && row[badgeFieldName] !== '') {
            return true;
        }

        // Check in parsed data field
        if (row.data) {
            let parsedData = null;
            if (typeof row.data === 'string') {
                try {
                    parsedData = JSON.parse(row.data);
                } catch (error) {
                    // Ignore parse errors
                }
            } else if (typeof row.data === 'object') {
                parsedData = row.data;
            }

            if (
                parsedData &&
                parsedData[badgeFieldName] !== undefined &&
                parsedData[badgeFieldName] !== null &&
                parsedData[badgeFieldName] !== ''
            ) {
                return true;
            }
        }
    }

    // Default: use isBadgeShow prop if available
    if (row.isBadgeShow !== undefined) {
        return row.isBadgeShow;
    }

    return false;
};

// GraphQL mutation for updating applicant fields
const UPDATE_APPLICANT_FIELD = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!, $field: String!, $value: String!) {
        result: updateApplicantField(eventId: $eventId, role: $role, id: $id, field: $field, value: $value)
    }
`;

// GraphQL mutation for deleting applicants
const DELETE_APPLICANT = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!, $deletionReason: String) {
        result: deleteApplicant(eventId: $eventId, role: $role, id: $id, deletionReason: $deletionReason)
    }
`;

const AdvancedDataTable = ({
    data = [],
    columns = [],
    title = 'Data Table',
    pageSize = 25,
    pageSizeOptions = [10, 25, 50, 100],
    maxHeight = 600,
    isAdmin = false,
    permissions = [],
    tableRole = '',
    eventId = '', // Add eventId prop
    features = [], // Add features prop for badge eligibility
    exportConfig = {},
    onExport,
    showSearch = true,
    showFilters = true,
    showPagination = true,
    showChips = true,
    chipColumns = [], // Columns that should display as chips
    filterColumns = [], // Columns that should have filters
    sortableColumns = [], // Columns that should be sortable
    searchableColumns = [], // Columns that should be searchable
    isModal = false, // New prop to indicate if this is a modal table
    isEventChoicesEnabled = false, // Add Event Choices feature flag
    isPaymentEnable = false, // Add Payment feature flag - Updated
    isEmailVerificationEnabled = false, // Add Email Verification feature flag
    selectedIds = [], // Parent component's selected IDs
    onSelectionChange = () => {}, // Parent component's selection handler
    onRefetch = () => {}, // Parent component's refetch handler
    hideActions = false, // Hide action column for read-only tables
    emailStatusMap = {}, // Email status map for displaying email status icons
}) => {
    // Props logged for debugging (commented out for performance)

    // Force recompilation - component updated - v2

    // Filter out 'updated' column and set initial visible columns
    const filteredColumns = columns.filter(col => col.key !== 'updated');

    // State management
    const [currentPage, setCurrentPage] = useState(0);
    // Initialize rowsPerPage with reasonable default for performance
    const [rowsPerPage, setRowsPerPage] = useState(pageSize || 100);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filters, setFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState(
        filteredColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const isInitialMount = React.useRef(true);

    // Remove auto-expanding pagination for better performance
    // Users can manually increase page size if needed
    const [individualEmailOpen, setIndividualEmailOpen] = useState(false);
    const [selectedRowForEmail, setSelectedRowForEmail] = useState(null);
    const [emailComponentKey, setEmailComponentKey] = useState(0);
    const [editingRow, setEditingRow] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [rowToDelete, setRowToDelete] = useState(null);
    const theme = useTheme();
    // GraphQL mutation for updating fields
    const [updateField] = useMutation(UPDATE_APPLICANT_FIELD, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data?.result || 'Updated successfully',
                severity: 'success',
            });
            setEditingRow(null);
            setEditValues({});
            onRefetch(); // Refetch data to show updated values
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Update failed',
                severity: 'error',
            });
        },
    });

    // GraphQL mutation for deleting applicants
    const [deleteApplicant] = useMutation(DELETE_APPLICANT, {
        onCompleted: data => {
            setSnackbar({
                open: true,
                message: data?.result || 'Record deleted successfully',
                severity: 'success',
            });
            setDeleteDialogOpen(false);
            setDeleteReason('');
            setRowToDelete(null);
            onRefetch(); // Refetch data to show updated values
        },
        onError: error => {
            setSnackbar({
                open: true,
                message: error.message || 'Delete failed',
                severity: 'error',
            });
        },
    });

    // Date formatting function
    const formatDate = dateValue => {
        if (!dateValue) return '';

        try {
            let timestamp = dateValue;

            // Convert string timestamps to numbers
            if (typeof timestamp === 'string' && /^\d+$/.test(timestamp)) {
                timestamp = parseInt(timestamp, 10);
            }

            // If timestamp is too large (future date), convert from milliseconds to seconds
            if (timestamp > 2000000000) {
                timestamp = Math.floor(timestamp / 1000);
            }

            const date = new Date(timestamp * 1000);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }

            return format(date, 'dd-MMM-yyyy hh:mm a');
        } catch (error) {
            console.error('Date formatting error:', error, 'Original value:', dateValue);
            return 'Invalid Date';
        }
    };

    // Reset individual email state when modal closes
    React.useEffect(() => {
        if (!individualEmailOpen) {
            setSelectedRowForEmail(null);
        }
    }, [individualEmailOpen]);

    // Handle individual email modal close
    const handleIndividualEmailClose = () => {
        setIndividualEmailOpen(false);
        setSelectedRowForEmail(null);
    };

    // Check if export is allowed - admin, superadmin, or export-{role} permission
    const canExport = (() => {
        // Check for disableExport first
        if (permissions.includes('disableExport')) {
            return false;
        }

        // Check for admin or superadmin
        if (permissions.includes('admin') || permissions.includes('superadmin')) {
            return true;
        }

        // Check for role-specific export permission (export-{role})
        if (tableRole && permissions.includes('export-' + tableRole)) {
            return true;
        }

        // Check for legacy exp-{role} permission
        if (tableRole && permissions.includes('exp-' + tableRole)) {
            return true;
        }

        return false;
    })();

    // Memoized filtered and sorted data
    const processedData = useMemo(() => {
        let filteredData = [...data];

        // Apply search
        if (searchTerm && searchableColumns.length > 0) {
            filteredData = filteredData.filter(row =>
                searchableColumns.some(col => {
                    // Handle nested object properties (e.g., 'footfallData.created')
                    let value;
                    if (col.includes('.')) {
                        const keys = col.split('.');
                        value = keys.reduce((obj, key) => obj && obj[key], row);
                    } else {
                        value = row[col];
                    }

                    if (value === null || value === undefined) return false;
                    return String(value).toLowerCase().includes(searchTerm.toLowerCase());
                })
            );
        }

        // Apply filters
        Object.entries(filters).forEach(([key, filterValue]) => {
            if (filterValue && filterValue.length > 0) {
                filteredData = filteredData.filter(row => {
                    // Handle nested object properties (e.g., 'footfallData.created')
                    let value;
                    if (key.includes('.')) {
                        const keys = key.split('.');
                        value = keys.reduce((obj, key) => obj && obj[key], row);
                    } else {
                        value = row[key];
                    }

                    if (value === null || value === undefined) return false;

                    // Handle comma-separated values
                    if (typeof value === 'string' && value.includes(',')) {
                        const values = value.split(',').map(v => v.trim());
                        return filterValue.some(fv => values.includes(fv));
                    }

                    // Handle created field date conversion for filtering
                    if (key === 'created' && typeof value === 'string' && /^\d{10,13}$/.test(value)) {
                        try {
                            let timestamp = parseInt(value, 10);
                            if (timestamp > 2000000000) {
                                timestamp = Math.floor(timestamp / 1000);
                            }
                            const date = new Date(timestamp * 1000);
                            if (!isNaN(date.getTime())) {
                                const formattedDate = format(date, 'dd-MMM-yyyy');
                                return filterValue.includes(formattedDate);
                            }
                        } catch (error) {
                            // Fall through to default comparison
                        }
                    }

                    return filterValue.includes(value);
                });
            }
        });

        // Apply sorting
        if (sortConfig.key) {
            filteredData.sort((a, b) => {
                // Handle nested object properties (e.g., 'footfallData.created')
                let aVal, bVal;
                if (sortConfig.key.includes('.')) {
                    const keys = sortConfig.key.split('.');
                    aVal = keys.reduce((obj, key) => obj && obj[key], a);
                    bVal = keys.reduce((obj, key) => obj && obj[key], b);
                } else {
                    aVal = a[sortConfig.key];
                    bVal = b[sortConfig.key];
                }

                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;

                const comparison = String(aVal).localeCompare(String(bVal));
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }

        return filteredData;
    }, [data, searchTerm, filters, sortConfig, searchableColumns]);

    // Memoized paginated data
    const paginatedData = useMemo(() => {
        // If rowsPerPage equals or exceeds total data length, show all data
        if (rowsPerPage >= processedData.length) {
            return processedData;
        }

        // If pagination is disabled, show all data
        if (!showPagination) return processedData;

        const start = currentPage * rowsPerPage;
        const end = start + rowsPerPage;
        return processedData.slice(start, end);
    }, [processedData, currentPage, rowsPerPage, showPagination]);

    // Helper functions
    const getUniqueValues = columnKey => {
        const values = new Set();
        data.forEach(row => {
            // Handle nested object properties (e.g., 'footfallData.created')
            let value;
            if (columnKey.includes('.')) {
                const keys = columnKey.split('.');
                value = keys.reduce((obj, key) => obj && obj[key], row);
            } else {
                value = row[columnKey];
            }

            if (value !== null && value !== undefined) {
                if (typeof value === 'string' && value.includes(',')) {
                    value.split(',').forEach(v => values.add(v.trim()));
                } else {
                    // Format dates for created field
                    if (columnKey === 'created' && typeof value === 'string' && /^\d{10,13}$/.test(value)) {
                        try {
                            let timestamp = parseInt(value, 10);
                            if (timestamp > 2000000000) {
                                timestamp = Math.floor(timestamp / 1000);
                            }
                            const date = new Date(timestamp * 1000);
                            if (!isNaN(date.getTime())) {
                                values.add(format(date, 'dd-MMM-yyyy'));
                            } else {
                                values.add(String(value));
                            }
                        } catch (error) {
                            values.add(String(value));
                        }
                    } else {
                        values.add(String(value));
                    }
                }
            }
        });
        return Array.from(values).sort();
    };

    const handleSort = key => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleFilterChange = (columnKey, value) => {
        setFilters(prev => ({
            ...prev,
            [columnKey]: value,
        }));
        setCurrentPage(0);
    };

    const handleSearchChange = event => {
        setSearchTerm(event.target.value);
        setCurrentPage(0);
    };

    const handleExport = async format => {
        if (!onExport) return;

        setExporting(true);
        try {
            // Export ALL data, not just processedData (which is filtered/paginated)
            // This ensures all 150 records are exported even if table shows only 15
            await onExport('csv', data);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setExporting(false);
        }
    };

    // Helper function to get payment status indicator - only show for successful payments
    const getPaymentStatusIndicator = paymentStatus => {
        if (!paymentStatus) return null;

        const status = paymentStatus.toLowerCase();
        // Only show badge for successful payments
        if (status === 'success') {
            return (
                <Tooltip title='Payment Successful'>
                    <CheckCircle color='success' fontSize='small' />
                </Tooltip>
            );
        }
        return null;
    };

    // Helper function to get event status indicator
    const getEventStatusIndicator = (eventDateFrom, eventDateTo) => {
        if (!eventDateFrom || !eventDateTo) return null;

        try {
            const now = new Date();
            // The dates are already in milliseconds, so no need to multiply by 1000
            const startDate = new Date(parseInt(eventDateFrom));
            const endDate = new Date(parseInt(eventDateTo));

            if (now < startDate) {
                const daysLeft = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
                return (
                    <Tooltip title={`Event starts in ${daysLeft} days`}>
                        <Schedule color='info' fontSize='small' />
                    </Tooltip>
                );
            } else if (now >= startDate && now <= endDate) {
                return (
                    <Tooltip title='Event is live'>
                        <EventAvailable color='success' fontSize='small' />
                    </Tooltip>
                );
            } else {
                const daysAgo = Math.ceil((now - endDate) / (1000 * 60 * 60 * 24));
                return (
                    <Tooltip title={`Event ended ${daysAgo} days ago`}>
                        <EventBusy color='error' fontSize='small' />
                    </Tooltip>
                );
            }
        } catch (error) {
            return null;
        }
    };

    // Helper function to get email status icon
    const getEmailStatusIcon = row => {
        if (!emailStatusMap || Object.keys(emailStatusMap).length === 0) return null;

        // Try to find email in row (check common email field names)
        const email = row.email || row.Email || row.EMAIL || row.recipient || row.Recipient;
        if (!email) return null;

        const status = emailStatusMap[email]?.toLowerCase() || emailStatusMap[email.toLowerCase()]?.toLowerCase();
        if (!status) return null;

        const statusConfig = {
            sent: { icon: EmailOutlined, color: 'default', label: 'Sent' },
            delivered: { icon: MarkEmailRead, color: 'info', label: 'Delivered' },
            opened: { icon: MarkEmailRead, color: 'success', label: 'Opened' },
            clicked: { icon: MarkEmailRead, color: 'primary', label: 'Clicked' },
            bounced: { icon: ReportProblem, color: 'warning', label: 'Bounced' },
            rejected: { icon: Block, color: 'error', label: 'Rejected' },
            spam: { icon: Warning, color: 'warning', label: 'Marked as Spam' },
            unsubscribed: { icon: Block, color: 'error', label: 'Unsubscribed' },
        };

        const config = statusConfig[status];
        if (!config) return null;

        const IconComponent = config.icon;
        return (
            <Tooltip title={config.label} arrow>
                <IconComponent color={config.color} fontSize='small' sx={{ cursor: 'help' }} />
            </Tooltip>
        );
    };

    // Render cell content with chip support
    const renderCellContent = (row, column, rowIndex) => {
        // Handle nested object properties (e.g., 'footfallData.created')
        let value;
        if (column.key.includes('.')) {
            const keys = column.key.split('.');
            value = keys.reduce((obj, key) => obj && obj[key], row);
        } else {
            value = row[column.key];
        }

        // Handle edit mode - exclude system columns and event columns
        const nonEditableColumns = [
            'id',
            'created',
            'regLink',
            'actions',
            'venue_location',
            'event_date_from',
            'event_date_to',
            'event_status',
        ];

        // Allow editing payment_status and status when features are enabled
        if (isPaymentEnable && column.key === 'payment_status') {
            // Allow editing
        } else if (isEmailVerificationEnabled && column.key === 'status') {
            // Allow editing
        } else if (nonEditableColumns.includes(column.key)) {
            // Don't allow editing for non-editable columns
        }

        if (editingRow === rowIndex && !nonEditableColumns.includes(column.key)) {
            // Render dropdown for payment_status when payment is enabled
            if (isPaymentEnable && column.key === 'payment_status') {
                return (
                    <Select
                        size='small'
                        value={editValues[column.key] || row[column.key] || ''}
                        onChange={e => {
                            const newValue = e.target.value;
                            setEditValues(prev => ({
                                ...prev,
                                [column.key]: newValue,
                            }));
                            // Auto-save on change
                            if (newValue !== row[column.key]) {
                                updateField({
                                    variables: {
                                        eventId,
                                        role: tableRole,
                                        id: row.id,
                                        field: column.key,
                                        value: newValue,
                                    },
                                });
                                setEditingRow(null);
                                setEditValues({});
                            }
                        }}
                        onBlur={() => {
                            setEditingRow(null);
                            setEditValues({});
                        }}
                        sx={{ minWidth: 120 }}
                    >
                        <MenuItem value='Success'>Success</MenuItem>
                        <MenuItem value='Initiated'>Initiated</MenuItem>
                        <MenuItem value='Failed'>Failed</MenuItem>
                    </Select>
                );
            }

            // Render dropdown for status when email verification is enabled
            if (isEmailVerificationEnabled && column.key === 'status') {
                return (
                    <Select
                        size='small'
                        value={editValues[column.key] || row[column.key] || ''}
                        onChange={e => {
                            const newValue = e.target.value;
                            setEditValues(prev => ({
                                ...prev,
                                [column.key]: newValue,
                            }));
                            // Auto-save on change
                            if (newValue !== row[column.key]) {
                                updateField({
                                    variables: {
                                        eventId,
                                        role: tableRole,
                                        id: row.id,
                                        field: column.key,
                                        value: newValue,
                                    },
                                });
                                setEditingRow(null);
                                setEditValues({});
                            }
                        }}
                        onBlur={() => {
                            setEditingRow(null);
                            setEditValues({});
                        }}
                        sx={{ minWidth: 120 }}
                    >
                        <MenuItem value='accept'>Accept</MenuItem>
                        <MenuItem value='pending'>Pending</MenuItem>
                        <MenuItem value='reject'>Reject</MenuItem>
                    </Select>
                );
            }

            // Default text field for other editable columns
            return (
                <TextField
                    size='small'
                    value={editValues[column.key] || ''}
                    onChange={e => {
                        setEditValues(prev => ({
                            ...prev,
                            [column.key]: e.target.value,
                        }));
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            // Save changes on Enter
                            const newValue = editValues[column.key] || e.target.value;
                            if (newValue !== row[column.key]) {
                                updateField({
                                    variables: {
                                        eventId,
                                        role: tableRole,
                                        id: row.id,
                                        field: column.key,
                                        value: newValue,
                                    },
                                });
                            } else {
                                setEditingRow(null);
                                setEditValues({});
                            }
                        } else if (e.key === 'Escape') {
                            // Cancel edit on Escape
                            setEditingRow(null);
                            setEditValues({});
                        }
                    }}
                    sx={{ minWidth: 120 }}
                />
            );
        }

        if (value === null || value === undefined || value === '') {
            return (
                <Typography variant='body2' color='text.secondary'>
                    -
                </Typography>
            );
        }

        // Handle date formatting for both ISO strings and timestamps
        // Only format as date if it's specifically a date field or looks like a timestamp
        if (typeof value === 'string' && (value.includes('T') || value.includes('Z') || /^\d{10,13}$/.test(value))) {
            // Only treat as timestamp if it's 10-13 digits (valid timestamp range)
            if (/^\d{10,13}$/.test(value)) {
                try {
                    let timestamp = parseInt(value, 10);

                    // Check if it's a valid timestamp (between 2000 and 2100)
                    const year2000 = new Date('2000-01-01').getTime() / 1000;
                    const year2100 = new Date('2100-01-01').getTime() / 1000;

                    // If timestamp is too large (future date), convert from milliseconds to seconds
                    if (timestamp > 2000000000) {
                        timestamp = Math.floor(timestamp / 1000);
                    }

                    // Only format if it's a reasonable timestamp
                    if (timestamp >= year2000 && timestamp <= year2100) {
                        const date = new Date(timestamp * 1000);
                        if (!isNaN(date.getTime())) {
                            return <Typography variant='body2'>{format(date, 'dd-MMM-yyyy hh:mm a')}</Typography>;
                        }
                    }
                } catch (error) {
                    // If date parsing fails, continue with normal rendering
                }
            } else if (value.includes('T') || value.includes('Z')) {
                // Handle ISO date strings
                try {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        return <Typography variant='body2'>{format(date, 'dd-MMM-yyyy hh:mm a')}</Typography>;
                    }
                } catch (error) {
                    // If date parsing fails, continue with normal rendering
                }
            }
        }

        // Handle comma-separated values as chips (or count for printTimes/scanTimes)
        if (showChips && chipColumns.includes(column.key) && value) {
            // Check if we have full timestamp data for tooltips (printTimes or scanTimes)
            const timesData = row.printTimesData || row.scanTimesData || [];

            // Special handling for printTimes/scanTimes - show count with tooltip of all times
            if ((column.key === 'printTimes' || column.key === 'scanTimes') && timesData.length > 0) {
                const count = timesData.length;
                const allTimes = timesData.map(t => t.full || t.time || t).join('\n');
                const chip = (
                    <Chip
                        label={count}
                        size='small'
                        color='primary'
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            minWidth: 32,
                        }}
                    />
                );

                return (
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant='caption' display='block' sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {column.key === 'printTimes' ? 'Print Times' : 'Scan Times'} ({count}):
                                </Typography>
                                {timesData.map((t, idx) => (
                                    <Typography key={idx} variant='caption' display='block'>
                                        {t.full || `${t.date} ${t.time}` || t}
                                    </Typography>
                                ))}
                            </Box>
                        }
                        arrow
                        placement='top'
                    >
                        {chip}
                    </Tooltip>
                );
            }

            // Split by comma if it's a string, otherwise treat as single value
            const values =
                typeof value === 'string' && value.includes(',')
                    ? value.split(',').map(v => v.trim())
                    : [String(value)];

            return (
                <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                    {values.map((v, index) => {
                        const chip = (
                            <Chip key={index} label={v} size='small' variant='outlined' sx={{ fontSize: '0.75rem' }} />
                        );

                        // Add tooltip if we have full timestamp data
                        if (timesData[index]) {
                            return (
                                <Tooltip key={index} title={timesData[index].full || v} arrow>
                                    {chip}
                                </Tooltip>
                            );
                        }

                        return chip;
                    })}
                </Stack>
            );
        }

        // Handle payment_status with dropdown display when payment is enabled
        if (isPaymentEnable && column.key === 'payment_status') {
            const paymentStatusOptions = ['Success', 'Initiated', 'Failed'];
            const currentValue = value || '';
            const isValidValue = paymentStatusOptions.includes(currentValue);

            if (!isValidValue && currentValue !== '') {
                return (
                    <Chip
                        label={currentValue}
                        size='small'
                        color='error'
                        variant='outlined'
                        sx={{ fontSize: '0.75rem' }}
                    />
                );
            }

            const getPaymentStatusColor = status => {
                switch (status) {
                    case 'Success':
                        return 'success';
                    case 'Initiated':
                        return 'warning';
                    case 'Failed':
                        return 'error';
                    default:
                        return 'default';
                }
            };

            return (
                <Chip
                    label={currentValue || '-'}
                    size='small'
                    color={getPaymentStatusColor(currentValue)}
                    variant='outlined'
                    sx={{ fontSize: '0.75rem' }}
                />
            );
        }

        // Handle status with dropdown display when email verification is enabled
        if (isEmailVerificationEnabled && column.key === 'status') {
            const statusOptions = ['accept', 'pending', 'reject'];
            const currentValue = (value || '').toLowerCase();
            const isValidValue = statusOptions.includes(currentValue);

            if (!isValidValue && currentValue !== '') {
                return (
                    <Chip
                        label={value || '-'}
                        size='small'
                        color='error'
                        variant='outlined'
                        sx={{ fontSize: '0.75rem' }}
                    />
                );
            }

            const getStatusColor = status => {
                switch (status) {
                    case 'accept':
                        return 'success';
                    case 'pending':
                        return 'warning';
                    case 'reject':
                        return 'error';
                    default:
                        return 'default';
                }
            };

            return (
                <Chip
                    label={currentValue || '-'}
                    size='small'
                    color={getStatusColor(currentValue)}
                    variant='outlined'
                    sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}
                />
            );
        }

        // Handle event status with icons and colors
        if (column.key === 'event_status' && isEventChoicesEnabled) {
            if (!value) {
                return (
                    <Typography variant='body2' color='text.secondary'>
                        -
                    </Typography>
                );
            }

            const getEventStatusDisplay = status => {
                switch (status) {
                    case 'upcoming':
                        return {
                            icon: <Schedule color='primary' fontSize='small' />,
                            text: 'Upcoming',
                            color: 'primary.main',
                            bgColor: 'primary.light',
                        };
                    case 'live':
                        return {
                            icon: <EventAvailable color='success' fontSize='small' />,
                            text: 'Live',
                            color: 'success.main',
                            bgColor: 'success.light',
                        };
                    case 'closed':
                        return {
                            icon: <EventBusy color='error' fontSize='small' />,
                            text: 'Closed',
                            color: 'error.main',
                            bgColor: 'error.light',
                        };
                    default:
                        return {
                            icon: <Schedule color='info' fontSize='small' />,
                            text: status,
                            color: 'info.main',
                            bgColor: 'info.light',
                        };
                }
            };

            const statusDisplay = getEventStatusDisplay(value);

            return (
                <Stack direction='row' spacing={1} alignItems='center'>
                    {statusDisplay.icon}
                    <Typography
                        variant='body2'
                        sx={{
                            color: statusDisplay.color,
                            fontWeight: 600,
                        }}
                    >
                        {statusDisplay.text}
                    </Typography>
                </Stack>
            );
        }

        // Handle long text
        if (typeof value === 'string' && value.length > 50) {
            return (
                <Tooltip title={value}>
                    <Typography variant='body2' noWrap>
                        {value.substring(0, 50)}...
                    </Typography>
                </Tooltip>
            );
        }

        return <Typography variant='body2'>{String(value)}</Typography>;
    };

    // Render filter dropdown
    const renderFilterDropdown = column => {
        if (!filterColumns.includes(column.key)) return null;

        const uniqueValues = getUniqueValues(column.key);

        return (
            <Autocomplete
                multiple
                size='small'
                options={uniqueValues}
                value={filters[column.key] || []}
                onChange={(event, newValue) => handleFilterChange(column.key, newValue)}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={filters[column.key]?.length > 0 ? `${filters[column.key].length} selected` : 'All'}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: '1px',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'primary.dark',
                                    borderWidth: '2px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: '2px',
                                },
                            },
                        }}
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size='small'
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '& .MuiChip-deleteIcon': {
                                    color: 'white',
                                    '&:hover': {
                                        color: 'rgba(255,255,255,0.8)',
                                    },
                                },
                            }}
                        />
                    ))
                }
                renderOption={(props, option) => (
                    <Box component='li' {...props}>
                        <Typography variant='body2'>{option}</Typography>
                    </Box>
                )}
                sx={{
                    minWidth: 150,
                    '& .MuiAutocomplete-tag': {
                        margin: '2px',
                    },
                }}
            />
        );
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6'>{title}</Typography>
                <Stack direction='row' spacing={1}>
                    {/* Column Visibility Toggle */}
                    <Tooltip title='Toggle Column Visibility'>
                        <IconButton size='small' onClick={() => setShowColumnSelector(!showColumnSelector)}>
                            {showColumnSelector ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </Tooltip>

                    {/* Export Button - CSV Only */}
                    {canExport && (
                        <Button
                            size='small'
                            variant='outlined'
                            startIcon={<Download />}
                            onClick={() => handleExport('csv')}
                            disabled={exporting}
                        >
                            {exporting ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Column Selector */}
            {showColumnSelector && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant='subtitle2' gutterBottom>
                        Visible Columns
                    </Typography>
                    <Stack direction='row' spacing={2} flexWrap='wrap'>
                        {columns.map(column => (
                            <FormControlLabel
                                key={column.key}
                                control={
                                    <Checkbox
                                        checked={visibleColumns[column.key]}
                                        onChange={e =>
                                            setVisibleColumns(prev => ({
                                                ...prev,
                                                [column.key]: e.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label={column.label}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Search and Filters */}
            <Paper
                elevation={1}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: '3px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid',
                    borderColor: 'primary.light',
                }}
            >
                <Typography
                    variant='subtitle1'
                    gutterBottom
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    🔍 Search & Filter Options
                </Typography>

                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={3} alignItems='flex-end'>
                        {/* Search Field */}
                        {showSearch && (
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    placeholder='🔍 Search in table...'
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'primary.main',
                                                borderWidth: '1px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'primary.dark',
                                                borderWidth: '2px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                                borderWidth: '2px',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        )}

                        {/* Filter Dropdowns */}
                        {showFilters &&
                            filterColumns.map(columnKey => {
                                const column = filteredColumns.find(col => col.key === columnKey);
                                if (!column) return null;

                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={columnKey}>
                                        <Box>
                                            <Typography
                                                variant='caption'
                                                display='block'
                                                gutterBottom
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'primary.main',
                                                    mb: 1,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                🔽 {column.label}
                                            </Typography>
                                            {renderFilterDropdown(column)}
                                        </Box>
                                    </Grid>
                                );
                            })}
                    </Grid>
                </Box>
            </Paper>

            {/* Results Summary */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    px: 2,
                    py: 2,
                    bgcolor: 'rgba(102, 126, 234, 0.05)',
                    borderRadius: '3px',
                    border: '1px solid',
                    borderColor: 'primary.light',
                }}
            >
                <Chip
                    label={`📊 Total Records: ${processedData.length}`}
                    size='small'
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                    }}
                />
                {showPagination && (
                    <Chip
                        label={`⚙️ Pagination: ${showPagination ? 'Enabled' : 'Disabled'}`}
                        size='small'
                        variant='outlined'
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                        }}
                    />
                )}
            </Box>

            {/* Table */}
            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: showPagination ? maxHeight - 100 : maxHeight,
                    minHeight: 200,
                    border: '2px solid',
                    borderColor: 'primary.light',
                    borderRadius: '3px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '3px',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        },
                    },
                    // Responsive height adjustments
                    [theme.breakpoints.down('sm')]: {
                        maxHeight: Math.min(maxHeight, 400),
                    },
                    [theme.breakpoints.down('md')]: {
                        maxHeight: Math.min(maxHeight, 500),
                    },
                }}
            >
                <Table
                    stickyHeader={true}
                    size='small'
                    sx={{
                        minWidth: { xs: 300, sm: 650 },
                        '& .MuiTableHead-root': {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        },
                        [theme.breakpoints.up('md')]: {
                            stickyHeader: true,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {/* Selection Checkbox */}
                            {!isModal &&
                                (permissions.includes('admin') ||
                                    permissions.includes('bulk-email') ||
                                    permissions.includes('bulk-print')) && (
                                    <TableCell padding='checkbox'>
                                        <Checkbox
                                            checked={
                                                selectedRows.length === paginatedData.length && paginatedData.length > 0
                                            }
                                            indeterminate={
                                                selectedRows.length > 0 && selectedRows.length < paginatedData.length
                                            }
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    // console.log('paginatedData', paginatedData);
                                                    const newSelection = paginatedData.map((row, index) => {
                                                        // Handle both flattened data and JSON data
                                                        let email = '';
                                                        // console.log('row', row);
                                                        if (row.data && typeof row.data === 'string') {
                                                            try {
                                                                const parsedData = JSON.parse(row.data);
                                                                email = parsedData.email || parsedData.Email || '';
                                                            } catch (error) {
                                                                console.warn(
                                                                    'Failed to parse row.data as JSON:',
                                                                    error
                                                                );
                                                                email = '';
                                                            }
                                                        } else if (row.data && typeof row.data === 'object') {
                                                            // Data is already an object
                                                            email = row.data.email || row.data.Email || '';
                                                        } else {
                                                            // Data is already flattened
                                                            email = row.email || row.Email || '';
                                                        }
                                                        return {
                                                            id: row.id,
                                                            email: email,
                                                        };
                                                    });
                                                    setSelectedRows(newSelection);
                                                    onSelectionChange(newSelection);
                                                } else {
                                                    setSelectedRows([]);
                                                    onSelectionChange([]);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                )}

                            {/* Actions Column - First */}
                            {!hideActions &&
                                !isModal &&
                                (permissions.includes('admin') ||
                                    permissions.includes('del-' + tableRole) ||
                                    permissions.includes('badge-' + tableRole) ||
                                    permissions.includes('email-' + tableRole)) && (
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            bgcolor: 'primary.light',
                                            color: 'primary.contrastText',
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main',
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                )}

                            {/* Data Columns */}
                            {filteredColumns
                                .filter(col => visibleColumns[col.key])
                                .map(column => (
                                    <TableCell
                                        key={column.key}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            bgcolor: 'primary.light',
                                            color: 'primary.contrastText',
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main',
                                            minWidth: column.minWidth || 120,
                                            width: column.width || 120,
                                        }}
                                    >
                                        {sortableColumns.includes(column.key) ? (
                                            <TableSortLabel
                                                active={sortConfig.key === column.key}
                                                direction={sortConfig.key === column.key ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort(column.key)}
                                                sx={{ color: 'inherit' }}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    hover
                                    sx={{
                                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                                        '&:hover': { bgcolor: 'primary.light', opacity: 0.8 },
                                        transition: 'background-color 0.2s ease',
                                    }}
                                >
                                    {/* Selection Checkbox */}
                                    {!isModal &&
                                        (permissions.includes('admin') ||
                                            permissions.includes('bulk-email') ||
                                            permissions.includes('bulk-print')) && (
                                            <TableCell padding='checkbox'>
                                                <Checkbox
                                                    checked={selectedRows.some(selected => selected.id === row.id)}
                                                    onChange={e => {
                                                        // Handle both flattened data and JSON data
                                                        let email = '';
                                                        if (row.data && typeof row.data === 'string') {
                                                            try {
                                                                const parsedData = JSON.parse(row.data);
                                                                email = parsedData.email || parsedData.Email || '';
                                                            } catch (error) {
                                                                console.warn(
                                                                    'Failed to parse row.data as JSON:',
                                                                    error
                                                                );
                                                                email = '';
                                                            }
                                                        } else if (row.data && typeof row.data === 'object') {
                                                            // Data is already an object
                                                            email = row.data.email || row.data.Email || '';
                                                        } else {
                                                            // Data is already flattened
                                                            email = row.email || row.Email || '';
                                                        }

                                                        const rowSelection = {
                                                            id: row.id,
                                                            email: email,
                                                        };

                                                        if (e.target.checked) {
                                                            const newSelection = [...selectedRows, rowSelection];
                                                            setSelectedRows(newSelection);
                                                            onSelectionChange(newSelection);
                                                        } else {
                                                            const newSelection = selectedRows.filter(
                                                                selected => selected.id !== row.id
                                                            );
                                                            setSelectedRows(newSelection);
                                                            onSelectionChange(newSelection);
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                        )}

                                    {/* Actions Column - First */}
                                    {!hideActions &&
                                        !isModal &&
                                        (permissions.includes('admin') ||
                                            permissions.includes('del-' + tableRole) ||
                                            permissions.includes('badge-' + tableRole) ||
                                            permissions.includes('email-' + tableRole)) && (
                                            <TableCell
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    minWidth: 200,
                                                    width: 200,
                                                }}
                                            >
                                                <Stack direction='row' spacing={1} alignItems='center'>
                                                    {/* Email Status Icon */}
                                                    {getEmailStatusIcon(row)}

                                                    {/* Badge Icon - Show based on pure function logic */}

                                                    {/* Edit Action */}
                                                    {permissions.includes('admin') ||
                                                    permissions.includes('edit-' + tableRole) ? (
                                                        <Tooltip
                                                            title={editingRow === rowIndex ? 'Save Changes' : 'Edit'}
                                                        >
                                                            <IconButton
                                                                size='small'
                                                                color={editingRow === rowIndex ? 'success' : 'info'}
                                                                onClick={() => {
                                                                    if (editingRow === rowIndex) {
                                                                        // Save changes - find changed fields and update them
                                                                        Object.keys(editValues).forEach(fieldKey => {
                                                                            const newValue = editValues[fieldKey];
                                                                            if (newValue !== row[fieldKey]) {
                                                                                updateField({
                                                                                    variables: {
                                                                                        eventId,
                                                                                        role: tableRole,
                                                                                        id: row.id,
                                                                                        field: fieldKey,
                                                                                        value: newValue,
                                                                                    },
                                                                                });
                                                                            }
                                                                        });
                                                                    } else {
                                                                        // Start editing
                                                                        setEditingRow(rowIndex);
                                                                        setEditValues({ ...row });
                                                                    }
                                                                }}
                                                            >
                                                                {editingRow === rowIndex ? <Save /> : <Edit />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}

                                                    {/* Cancel Edit Action */}
                                                    {editingRow === rowIndex && (
                                                        <Tooltip title='Cancel Edit'>
                                                            <IconButton
                                                                size='small'
                                                                color='error'
                                                                onClick={() => {
                                                                    setEditingRow(null);
                                                                    setEditValues({});
                                                                }}
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Delete Action */}
                                                    {permissions.includes('admin') ||
                                                    permissions.includes('del-' + tableRole) ? (
                                                        <Tooltip title='Delete'>
                                                            <IconButton
                                                                size='small'
                                                                color='error'
                                                                onClick={() => {
                                                                    setRowToDelete(row);
                                                                    setDeleteDialogOpen(true);
                                                                }}
                                                            >
                                                                <DeleteTwoTone />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}

                                                    {/* Badge Action */}
                                                    {/* {isBadgeEligible(
                                                        row,
                                                        eventId,
                                                        tableRole,
                                                        features,
                                                        permissions
                                                    ) && (
                                                        <Tooltip title='Eligible for Badge'>
                                                            <CheckCircle color='success' fontSize='small' />
                                                        </Tooltip>
                                                    )} */}
                                                    {isBadgeEligible(
                                                        row,
                                                        eventId,
                                                        tableRole,
                                                        features,
                                                        permissions
                                                    ) && (
                                                        <Tooltip title='Download Badge'>
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={() => {
                                                                    // Download badge functionality
                                                                    const currentEventId =
                                                                        eventId ||
                                                                        row.eventId ||
                                                                        window.location.pathname.split('/')[2];
                                                                    const role = row.role || tableRole;
                                                                    const badgeUrl = `https://api-reg.eventegration.com/graphql/badge/${currentEventId}/${role}/${row.id}`;
                                                                    window.open(badgeUrl, '_blank');
                                                                }}
                                                            >
                                                                <BadgeTwoTone />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Payment Status Indicator */}
                                                    {isPaymentEnable &&
                                                        (() => {
                                                            let paymentStatus = '';
                                                            if (row.data && typeof row.data === 'string') {
                                                                try {
                                                                    const parsedData = JSON.parse(row.data);
                                                                    paymentStatus = parsedData.payment_status || '';
                                                                } catch (error) {
                                                                    console.warn(
                                                                        'Failed to parse row.data as JSON:',
                                                                        error
                                                                    );
                                                                    paymentStatus = '';
                                                                }
                                                            } else if (row.data && typeof row.data === 'object') {
                                                                // Data is already an object
                                                                paymentStatus = row.data.payment_status || '';
                                                            } else {
                                                                // Data is already flattened
                                                                paymentStatus = row.payment_status || '';
                                                            }
                                                            return getPaymentStatusIndicator(paymentStatus);
                                                        })()}

                                                    {/* Event Status Indicator */}
                                                    {/* {isEventChoicesEnabled &&
                                                        (() => {
                                                            return getEventStatusIndicator(
                                                                row.event_date_from,
                                                                row.event_date_to
                                                            );
                                                        })()} */}

                                                    {/* Email Action */}
                                                    {permissions.includes('admin') ||
                                                    permissions.includes('email-' + tableRole) ? (
                                                        <Tooltip title='Send Email'>
                                                            <IconButton
                                                                size='small'
                                                                color='secondary'
                                                                onClick={() => {
                                                                    // Open SendEmail modal for individual row
                                                                    let email = '';
                                                                    if (row.data && typeof row.data === 'string') {
                                                                        try {
                                                                            const parsedData = JSON.parse(row.data);
                                                                            email =
                                                                                parsedData.email ||
                                                                                parsedData.Email ||
                                                                                '';
                                                                        } catch (error) {
                                                                            console.warn(
                                                                                'Failed to parse row.data as JSON:',
                                                                                error
                                                                            );
                                                                            email = '';
                                                                        }
                                                                    } else if (
                                                                        row.data &&
                                                                        typeof row.data === 'object'
                                                                    ) {
                                                                        // Data is already an object
                                                                        email = row.data.email || row.data.Email || '';
                                                                    } else {
                                                                        // Data is already flattened
                                                                        email = row.email || row.Email || '';
                                                                    }
                                                                    setSelectedRowForEmail({
                                                                        ...row,
                                                                        email: email,
                                                                    });
                                                                    setIndividualEmailOpen(true);
                                                                    setEmailComponentKey(prev => prev + 1); // Force re-render
                                                                }}
                                                            >
                                                                <Email />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                        )}

                                    {/* Data Cells */}
                                    {filteredColumns
                                        .filter(col => visibleColumns[col.key])
                                        .map(column => (
                                            <TableCell
                                                key={column.key}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    minWidth: column.minWidth || 120,
                                                    width: column.width || 120,
                                                }}
                                            >
                                                {renderCellContent(row, column, rowIndex)}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        columns.filter(col => visibleColumns[col.key]).length +
                                        (permissions.includes('admin') ||
                                        permissions.includes('bulk-email') ||
                                        permissions.includes('bulk-print')
                                            ? 1
                                            : 0) +
                                        (permissions.includes('admin') ||
                                        permissions.includes('del-' + tableRole) ||
                                        permissions.includes('badge-' + tableRole) ||
                                        permissions.includes('email-' + tableRole)
                                            ? 1
                                            : 0)
                                    }
                                >
                                    <Alert severity='info' sx={{ my: 2 }}>
                                        No data found
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Bottom Pagination */}
            {showPagination && processedData.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                        p: 3,
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: '3px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    }}
                >
                    <Chip
                        label={
                            rowsPerPage >= processedData.length
                                ? `📊 Showing all ${processedData.length} records`
                                : `📄 Showing ${paginatedData.length} of ${processedData.length} records`
                        }
                        size='small'
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                        }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            label='📋 Show:'
                            size='small'
                            variant='outlined'
                            sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            }}
                        />
                        <Autocomplete
                            size='small'
                            options={[...pageSizeOptions, processedData.length]}
                            value={rowsPerPage}
                            onChange={(event, newValue) => {
                                // console.log('Setting rowsPerPage to:', newValue);
                                setRowsPerPage(newValue);
                                setCurrentPage(0);
                            }}
                            getOptionLabel={option => {
                                if (option === processedData.length) {
                                    return `All (${processedData.length})`;
                                }
                                return `${option} per page`;
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    placeholder='Select page size'
                                    sx={{
                                        minWidth: 160,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'primary.main',
                                                borderWidth: '1px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'primary.dark',
                                                borderWidth: '2px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                                borderWidth: '2px',
                                            },
                                        },
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component='li' {...props}>
                                    <Typography variant='body2'>
                                        {option === processedData.length ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>📊</span>
                                                <span>All ({processedData.length})</span>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>📄</span>
                                                <span>{option} per page</span>
                                            </Box>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            sx={{
                                minWidth: 160,
                            }}
                        />

                        {processedData.length > rowsPerPage && rowsPerPage < processedData.length && (
                            <Pagination
                                count={Math.ceil(processedData.length / rowsPerPage)}
                                page={currentPage + 1}
                                onChange={(event, page) => setCurrentPage(page - 1)}
                                showFirstButton
                                showLastButton
                                size='small'
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        fontSize: '0.875rem',
                                        minWidth: 32,
                                        height: 32,
                                    },
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* Individual Email Modal */}
            {individualEmailOpen && selectedRowForEmail && (
                <SendEmail
                    key={emailComponentKey}
                    selectedIds={[
                        {
                            id: selectedRowForEmail.id,
                            email: selectedRowForEmail.email || selectedRowForEmail.Email,
                        },
                    ]}
                    eventId={eventId}
                    role={tableRole}
                    autoOpen={true}
                    onClose={handleIndividualEmailClose}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setDeleteReason('');
                    setRowToDelete(null);
                }}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle>Delete Record</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Are you sure you want to delete this record? This action will first archive the record and then
                        delete it permanently.
                    </DialogContentText>
                    <TextField
                        margin='dense'
                        label='Reason for deletion'
                        fullWidth
                        variant='outlined'
                        value={deleteReason}
                        onChange={e => setDeleteReason(e.target.value)}
                        required
                        multiline
                        rows={3}
                        placeholder='Please provide a reason for deleting this record...'
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setDeleteReason('');
                            setRowToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (deleteReason.trim() && rowToDelete) {
                                deleteApplicant({
                                    variables: {
                                        eventId: eventId,
                                        role: tableRole,
                                        id: rowToDelete.id,
                                        deletionReason: deleteReason.trim(),
                                    },
                                });
                            }
                        }}
                        color='error'
                        disabled={!deleteReason.trim()}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdvancedDataTable;
